import React, { useEffect } from "react";
import ReserveBudgetReport from "../screens/ReserveBudgetReport";
import { useIdleClose } from "../hooks/useIdleClose";

// 2026-03-04 (Dave · Option A) — In-place Reserve Budget Report overlay.
//
// Replaces the old `<a target="_blank">` behaviour of the
// ControlsStrip's "Reserve Budget Report" pill. Rendering the report
// inline (as an overlay on top of the app) instead of in a new
// browser tab eliminates every transient-tab bug we've been chasing:
// no Shell unmount, no visibility-change idle-timer race, no
// sessionStorage guessing about where the user was, no popup-blocker
// edge cases. The overlay is fixed-inset, 100vw/100vh, and covers
// the entire viewport at z-index 1300 — the same layer as the
// scenario-history / attachments modals so it never fights them for
// stacking order.
//
// The escape hatches for users who WANT a persistent browser tab
// live inside the ReserveBudgetReport banner (the "Open in New Tab"
// pill next to Download PDF). Clicking that pill navigates the
// modal-hosted report to `#report/reserve-budget/{aid}…` in a real
// new tab, and this overlay stays open in the parent until closed.
export default function ReportModal({ aid, onClose }) {
  useIdleClose(!!aid, onClose);
  // Esc key closes the modal. We attach at the window level so it
  // fires even when focus is on an inner iframe / non-focusable
  // element inside the report.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    // Prevent background scroll while the overlay is open. Restore
    // on unmount so we never leak the lock (e.g., if the parent
    // unmounts the modal without calling onClose).
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  if (!aid) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Reserve Budget Report"
      data-testid="report-modal"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        // Solid white so the overlay COVERS the app underneath even
        // during the report's own preboot spinner window (which is
        // borrowed from index.html — see ReserveBudgetReport.jsx's
        // preboot lifecycle effect).
        background: "#fff",
        overflow: "auto",
        // Own scroll container so the report's own scroll works
        // predictably regardless of the parent's overflow state.
        WebkitOverflowScrolling: "touch",
      }}
    >
      <ReserveBudgetReport aid={aid} onClose={onClose} />
    </div>
  );
}
